import React, { useState, useEffect, useRef, useCallback } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { useTranslation } from 'react-i18next';
import { Calendar, CheckCircle2, Clock, XCircle, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { useUser } from '@/contexts/UserContext';
import { useHeader } from '@/contexts/HeaderContext';
import Loading from '../components/Loading';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const History = () => {
  const { t } = useTranslation();
  const { user: contextUser, loading: userLoading } = useUser();
  const { setHeaderProps } = useHeader();
  const userId = contextUser?.user_id || contextUser?.id;

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ done: 0, total: 0 });
  const [user, setUser] = useState(null);

  // Refs per listener
  const resumeListenerRef = useRef(null);
  const visibilityListenerRef = useRef(null);

  /* --------------------------------------------------
     FUNZIONE API
  -------------------------------------------------- */
  const fetchHistory = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API}/suggestion/history/${userId}?limit=30`);
      setHistory(response.data);

      const done = response.data.filter(s => s.status === 'done').length;
      setStats({ done, total: response.data.length });
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'done':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'postponed':
        return <Clock className="w-5 h-5 text-orange-500" />;
      case 'not_done':
        return <XCircle className="w-5 h-5 text-stone-400" />;
      default:
        return <Clock className="w-5 h-5 text-stone-400" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
  };

  /* --------------------------------------------------
     EFFECT
  -------------------------------------------------- */
  useEffect(() => {
    if (userLoading || !userId) return;

    setUser(contextUser);
    fetchHistory();

    const onResume = () => fetchHistory();
    resumeListenerRef.current = onResume;
    visibilityListenerRef.current = () => {
      if (document.visibilityState === 'visible') onResume();
    };

    CapacitorApp.addListener('resume', resumeListenerRef.current);
    window.addEventListener('visibilitychange', visibilityListenerRef.current);

    return () => {
      CapacitorApp.removeAllListeners();
      window.removeEventListener('visibilitychange', visibilityListenerRef.current);
    };
  }, [userLoading, userId, contextUser, fetchHistory]);

  useEffect(() => {
    setHeaderProps({
      title: t('history.title'),
      user: contextUser
    });
  }, [t, contextUser, setHeaderProps]);

  /* --------------------------------------------------
     RENDER (phone mock)
  -------------------------------------------------- */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center page-container">
        <Loading size="hundred" message={null} />
      </div>
    );
  }

  return (
    <div style={{ background: 'inherit', display: 'flex', justifyContent: 'center', fontFamily: 'Manrope-Variable, serif' }}>
      <div style={{ width: '100%', maxWidth: 'clamp(390px, 70vw, 560px)', margin: '0 auto', minHeight: 844, background: 'inherit', position: 'relative', paddingBottom: 80 }}>

        {/* Date groups */}
        <div style={{ padding: '0 24px 80px' }}>
          {history.length === 0 ? (
            <div style={{ paddingTop: 48, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--c-surface-card, #f5f3ef)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                <svg viewBox="0 0 24 24" style={{ width: 28, height: 28, stroke: '#bbb', fill: 'none', strokeWidth: 1.5 }}><path d="M12 8v4l3 3"/><circle cx="12" cy="12" r="10"/></svg>
              </div>
              <div style={{ fontFamily: 'Manrope-Variable', fontSize: 15, color: '#9a9a9a' }}>{t('history.empty')}</div>
              <div style={{ fontSize: 13, color: '#9a9a9a', marginTop: 8 }}>{t('history.emptyEncouragement')}</div>
            </div>
          ) : (
            (() => {
              const groups = history.reduce((acc, item) => {
                const key = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(item.date));
                if (!acc[key]) acc[key] = [];
                acc[key].push(item);
                return acc;
              }, {});

              return Object.entries(groups)
                .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                .map(([date, items], di) => (
                  <div key={date} style={{ paddingTop: di === 0 ? 0 : 8 }}>
                    <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace', fontSize: 8, letterSpacing: '0.3em', textTransform: 'uppercase', color: '#9a9a9a', paddingTop: 28, marginBottom: 4 }}>{date}</div>

                    {items.map((item, idx) => {
                      const statusKey = item.status === 'done' ? 'done' : (item.status === 'not_done' || item.status === 'skipped' ? 'skipped' : 'pending');
                      const stroke = statusKey === 'done' ? '#21a721' : (statusKey === 'skipped' ? '#c9c9c9' : '#0a0a0a');
                      const metaDate = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short' }).format(new Date(item.date));

                      return (
                        <div
                          key={item.suggestion_id}
                          data-testid={`history-item-${item.suggestion_id}`}
                          style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 0', borderTop: '1px solid #e0ddd6' }}
                        >
                          <div style={{ width: 20, height: 20, flexShrink: 0, marginTop: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
                              {/* circle filled with state colour */}
                              <circle cx="12" cy="12" r="10" fill={stroke} />

                              {statusKey === 'done' && (
                                <polyline
                                  points="9 12 11 14 15 10"
                                  stroke="white"
                                  strokeWidth="1.5"
                                  fill="none"
                                />
                              )}
                              {statusKey === 'pending' && (
                                <polyline
                                  points="12 6 12 12 16 14"
                                  stroke="white"
                                  strokeWidth="1.5"
                                  fill="none"
                                />
                              )}
                              {statusKey === 'skipped' && (
                                <line
                                  x1="8"
                                  y1="12"
                                  x2="16"
                                  y2="12"
                                  stroke="black"
                                  strokeWidth="1.5"
                                  fill="none"
                                />
                              )}
                            </svg>
                          </div>

                          <div style={{ flex: 1 }}>
                            <div style={{ fontFamily: 'Manrope-Variable', fontWeight:300, lineHeight: 1.35, color: (item.status === 'postponed' ? '#9a9a9a' : '#0a0a0a'), letterSpacing: '-0.005em' }}>{item.main_suggestion?.text}</div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace', fontSize: 8, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                              <span style={{ color: '#9a9a9a' }}>{metaDate}</span>
                              <span style={{ width: 3, height: 3, borderRadius: 99, background: '#e0ddd6', flexShrink: 0 }} />
                              <span style={{ color: statusKey === 'done' ? '#9a9a9a' : (statusKey === 'skipped' ? '#9a9a9a' : '#9a9a9a') }}>{t(`history.status.${item.status}`)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ));
            })()
          )}
        </div>

        {/* phone mock bottom removed — use global BottomNav */}


      </div>
    </div>
  );
};

export default History;